/* eslint-disable */
import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  TextField,
  Card,
  CardContent,
  Divider,
  Box,
  Chip,
  CircularProgress,
  FormControlLabel,
  Switch
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import AccountBalanceIcon from "@material-ui/icons/AccountBalance";
import MonetizationOnIcon from "@material-ui/icons/MonetizationOn";
import PersonIcon from "@material-ui/icons/Person";
import SaveIcon from "@material-ui/icons/Save";
import CloseIcon from "@material-ui/icons/Close";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import { SalaryService } from "_services/salary.service";

const useStyles = makeStyles(theme => ({
  dialogTitle: {
    backgroundColor: "#1976d2",
    color: "#fff",
    padding: theme.spacing(2)
  },
  sectionTitle: {
    fontWeight: "bold",
    marginBottom: theme.spacing(1),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1)
  },
  incomeHeader: {
    color: "#2e7d32",
    fontWeight: "bold"
  },
  outcomeHeader: {
    color: "#c62828",
    fontWeight: "bold"
  },
  itemRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "6px 0",
    borderBottom: "1px dashed #e0e0e0"
  },
  kpiBox: {
    borderRadius: 8,
    padding: theme.spacing(1.5),
    textAlign: "center"
  },
  totalIncomeBox: {
    backgroundColor: "#e8f5e9",
    border: "1px solid #a5d6a7"
  },
  totalOutcomeBox: {
    backgroundColor: "#ffebee",
    border: "1px solid #ffcdd2"
  },
  totalNetBox: {
    backgroundColor: "#e3f2fd",
    border: "1px solid #90caf9"
  }
}));

export default function SalaryPersonDetailModal({
  open,
  onClose,
  userName,
  calYear,
  calMonth,
  onSaved
}) {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [personData, setPersonData] = useState(null);
  const [incomes, setIncomes] = useState([]);
  const [outcomes, setOutcomes] = useState([]);
  const [isApproved, setIsApproved] = useState(false);

  useEffect(() => {
    if (open && userName && calYear && calMonth) {
      loadData();
    }
  }, [open, userName, calYear, calMonth]);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await SalaryService.getPersonDetail(
        userName,
        calYear,
        calMonth
      );
      if (res.data && res.data.success && res.data.data) {
        const d = res.data.data;
        setPersonData(d);
        setIncomes(d.incomes || []);
        setOutcomes(d.outcomes || []);
        setIsApproved(d.approve === "y");
      }
    } catch (err) {
      console.error("Error loading salary detail:", err);
      alert("ไม่สามารถโหลดข้อมูลเงินเดือนได้");
    } finally {
      setLoading(false);
    }
  };

  const handleIncomeChange = (id, val) => {
    const num = parseFloat(val) || 0;
    setIncomes(prev =>
      prev.map(item => (item.id === id ? { ...item, value: num } : item))
    );
  };

  const handleOutcomeChange = (id, val) => {
    const num = parseFloat(val) || 0;
    setOutcomes(prev =>
      prev.map(item => (item.id === id ? { ...item, value: num } : item))
    );
  };

  const totalIncome = incomes.reduce((sum, item) => sum + (item.value || 0), 0);
  const totalOutcome = outcomes.reduce(
    (sum, item) => sum + (item.value || 0),
    0
  );
  const netSalary = totalIncome - totalOutcome;

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        userName,
        calYear,
        calMonth,
        approve: isApproved ? "y" : "n",
        incomes: incomes.map(i => ({ id: i.id, value: i.value || 0 })),
        outcomes: outcomes.map(o => ({ id: o.id, value: o.value || 0 }))
      };

      const res = await SalaryService.savePersonDetail(payload);
      if (res.data && res.data.success) {
        alert("บันทึกข้อมูลเงินเดือนเรียบร้อยแล้ว");
        if (onSaved) onSaved();
        onClose();
      } else {
        alert(res.data?.message || "เกิดข้อผิดพลาดในการบันทึก");
      }
    } catch (err) {
      console.error("Error saving salary:", err);
      alert("เกิดข้อผิดพลาดในการบันทึกข้อมูล");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle className={classes.dialogTitle}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" style={{ fontWeight: "bold" }}>
            บันทึกข้อมูลเงินเดือนรายบุคคล
          </Typography>
          <Box display="flex" alignItems="center" gap={1}>
            <Chip
              label={`ประจำเดือน ${calMonth}/${calYear}`}
              style={{ backgroundColor: "#fff", color: "#1976d2", fontWeight: "bold" }}
            />
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box py={6} textAlign="center">
            <CircularProgress />
            <Typography style={{ marginTop: 8 }}>กำลังโหลดข้อมูล...</Typography>
          </Box>
        ) : personData ? (
          <Grid container spacing={2}>
            {/* Employee Profile Header */}
            <Grid item xs={12}>
              <Card variant="outlined" style={{ backgroundColor: "#fbfbfb" }}>
                <CardContent style={{ padding: "12px 16px" }}>
                  <Grid container spacing={1} alignItems="center">
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle1" style={{ fontWeight: "bold", color: "#1565c0" }}>
                        <PersonIcon style={{ verticalAlign: "middle", marginRight: 4 }} />
                        {personData.fullName}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        ตำแหน่ง: {personData.positionName || "-"} | กลุ่ม: {personData.typeGovName || "-"}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} style={{ textAlign: "right" }}>
                      <Typography variant="body2">
                        <AccountBalanceIcon style={{ verticalAlign: "middle", marginRight: 4, fontSize: 18 }} />
                        ธนาคาร: <strong>{personData.bankName || "ไม่ได้ระบุ"}</strong>
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        เลขที่บัญชี: <strong>{personData.accountNo || "-"}</strong>
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Income Section (Left) */}
            <Grid item xs={12} md={6}>
              <Card variant="outlined" style={{ height: "100%" }}>
                <CardContent>
                  <Typography variant="subtitle1" className={`${classes.sectionTitle} ${classes.incomeHeader}`}>
                    <MonetizationOnIcon fontSize="small" /> รายการได้ (Incomes)
                  </Typography>
                  <Divider style={{ marginBottom: 12 }} />

                  {incomes.map(item => (
                    <Box key={item.id} className={classes.itemRow}>
                      <Typography variant="body2" style={{ flex: 1, paddingRight: 8 }}>
                        {item.name}
                      </Typography>
                      <TextField
                        type="number"
                        size="small"
                        variant="outlined"
                        value={item.value || ""}
                        placeholder="0.00"
                        onChange={e => handleIncomeChange(item.id, e.target.value)}
                        inputProps={{
                          min: 0,
                          step: "0.01",
                          style: { textAlign: "right", width: 110, padding: "6px 8px" }
                        }}
                      />
                    </Box>
                  ))}

                  <Box mt={2} pt={1} display="flex" justifyContent="space-between" borderTop="2px solid #2e7d32">
                    <Typography variant="subtitle2" style={{ fontWeight: "bold" }}>
                      รวมรายการได้
                    </Typography>
                    <Typography variant="subtitle2" style={{ fontWeight: "bold", color: "#2e7d32" }}>
                      {totalIncome.toLocaleString("th-TH", { minimumFractionDigits: 2 })} บาท
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Outcome Section (Right) */}
            <Grid item xs={12} md={6}>
              <Card variant="outlined" style={{ height: "100%" }}>
                <CardContent>
                  <Typography variant="subtitle1" className={`${classes.sectionTitle} ${classes.outcomeHeader}`}>
                    <MonetizationOnIcon fontSize="small" /> รายการหัก (Deductions)
                  </Typography>
                  <Divider style={{ marginBottom: 12 }} />

                  {outcomes.map(item => (
                    <Box key={item.id} className={classes.itemRow}>
                      <Typography variant="body2" style={{ flex: 1, paddingRight: 8 }}>
                        {item.name}
                      </Typography>
                      <TextField
                        type="number"
                        size="small"
                        variant="outlined"
                        value={item.value || ""}
                        placeholder="0.00"
                        onChange={e => handleOutcomeChange(item.id, e.target.value)}
                        inputProps={{
                          min: 0,
                          step: "0.01",
                          style: { textAlign: "right", width: 110, padding: "6px 8px" }
                        }}
                      />
                    </Box>
                  ))}

                  <Box mt={2} pt={1} display="flex" justifyContent="space-between" borderTop="2px solid #c62828">
                    <Typography variant="subtitle2" style={{ fontWeight: "bold" }}>
                      รวมรายการหัก
                    </Typography>
                    <Typography variant="subtitle2" style={{ fontWeight: "bold", color: "#c62828" }}>
                      {totalOutcome.toLocaleString("th-TH", { minimumFractionDigits: 2 })} บาท
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Net Calculation Summary Cards */}
            <Grid item xs={12}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Box className={`${classes.kpiBox} ${classes.totalIncomeBox}`}>
                    <Typography variant="caption" color="textSecondary">
                      รวมรายได้ทั้งหมด
                    </Typography>
                    <Typography variant="h6" style={{ color: "#2e7d32", fontWeight: "bold" }}>
                      {totalIncome.toLocaleString("th-TH", { minimumFractionDigits: 2 })}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box className={`${classes.kpiBox} ${classes.totalOutcomeBox}`}>
                    <Typography variant="caption" color="textSecondary">
                      รวมรายการหักทั้งหมด
                    </Typography>
                    <Typography variant="h6" style={{ color: "#c62828", fontWeight: "bold" }}>
                      {totalOutcome.toLocaleString("th-TH", { minimumFractionDigits: 2 })}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box className={`${classes.kpiBox} ${classes.totalNetBox}`}>
                    <Typography variant="caption" color="textSecondary">
                      เงินเดือนจ่ายสุทธิ
                    </Typography>
                    <Typography variant="h6" style={{ color: "#1565c0", fontWeight: "bold" }}>
                      {netSalary.toLocaleString("th-TH", { minimumFractionDigits: 2 })}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Grid>

            {/* Approval Switch */}
            <Grid item xs={12}>
              <Box display="flex" alignItems="center" justifyContent="flex-end">
                <FormControlLabel
                  control={
                    <Switch
                      checked={isApproved}
                      onChange={e => setIsApproved(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Typography variant="body2" style={{ fontWeight: "bold" }}>
                      {isApproved ? "อนุมัติข้อมูลแล้ว (Approved)" : "ยังไม่อนุมัติ (Pending)"}
                    </Typography>
                  }
                />
              </Box>
            </Grid>
          </Grid>
        ) : null}
      </DialogContent>

      <DialogActions style={{ padding: "12px 24px" }}>
        <Button onClick={onClose} startIcon={<CloseIcon />}>
          ปิด
        </Button>
        <Button
          onClick={handleSave}
          color="primary"
          variant="contained"
          startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
          disabled={saving || loading}
        >
          {saving ? "กำลังบันทึก..." : "บันทึกข้อมูล"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

